import {
  ChangeDetectionStrategy,
  Component,
  inject,
  computed,
  signal,
  effect, ViewEncapsulation, OnDestroy
} from '@angular/core';
import { CommonModule } from '@angular/common';

import { ComposerStateService } from '@app/model-composer';
import { ModelConfigService } from '../services/model-config.service';
import { ElementSelectorComponent } from './element-selector.component';

import { ComposerViewMode } from '../enums/view-mode.enum';

import { VestaStateService } from '../../vesta/services/vesta-state.service';
import {ComposerPanelTabsComponent} from '@app/model-composer/components/composer-panel-tabs.component';
import {CompositionCustomizationComponent} from '@app/model-composer/components/composition-customization.component';
import {ElementCustomizationComponent} from '@app/model-composer/components/element-customization.component';
import {ComposerPanelStateService} from '@app/model-composer/services/composer-panel-state.service';
import {ComposerPanelTab} from '@app/model-composer/enums/composer-panel-tab.enum';
import {VestaComponent} from '@app/vesta';
import {environment} from '../../../../environments/environment';
import {CompositionSelectionService} from '@app/model-composer/services/composition-selection.service';
import {ComposerEmptyStateComponent} from '@app/model-composer/components/composer-empty-state.component';
import {ActivatedRoute} from '@angular/router';
import {toSignal} from '@angular/core/rxjs-interop';
import {catchError, distinctUntilChanged, filter, map, startWith, switchMap} from 'rxjs/operators';
import {CompositionPricingComponent} from '@app/model-composer/components/composition-pricing.component';
import {ComposerFooterComponent} from '@app/model-composer/components/composer-footer.component';
import {VisionnaireLoaderComponent} from '@app/model-composer/components/visionnaire-loader.component';
import {of} from 'rxjs';
import {TranslatePipe} from "@ngx-translate/core";
import {IpeDebug} from "@app/model-composer/ipe-debug";
import {AuthService} from "@service/auth/auth.service";

@Component({
  selector: 'app-composer-shell',
  standalone: true,
  imports: [
    CommonModule,
    ElementSelectorComponent,
    ComposerPanelTabsComponent,
    CompositionCustomizationComponent,
    ElementCustomizationComponent,
    VestaComponent,
    ComposerEmptyStateComponent,
    CompositionPricingComponent,
    ComposerFooterComponent,
    VisionnaireLoaderComponent,
    TranslatePipe
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    @if (isModelLoading()) {
      <app-visionnaire-loader class="ipe:w-full" />
    } @else {
      <div class="ipe:h-full ipe:w-full ipe:grid ipe:grid-cols-[1.25fr_1fr] ipe:min-h-0 font-walter">
        <section class="ipe:bg-white ipe:flex ipe:items-center ipe:justify-center ipe:min-h-0 ipe:relative">
          @if (!hasComposition()) {
            <app-composer-empty-state />
          }
          <app-vesta-composer [viewMode]="viewMode()"/>
          <div class="ipe:absolute ipe:top-6 ipe:right-6 ipe:flex ipe:bg-[#0000008a] ipe:text-white ipe:text-sm ipe:font-semibold ipe:rounded-sm ipe:overflow-hidden ipe:shadow-lg ipe:z-10">
            @for (mode of [ComposerViewMode.TwoD, ComposerViewMode.ThreeD]; track mode) {
              <button
                (click)="viewMode.set(mode)"
                [class.ipe:bg-black]="viewMode() === mode"
                class="ipe:px-4 ipe:py-2 ipe:transition-colors ipe:hover:bg-gray-500 ipe:cursor-pointer">
                {{ mode === ComposerViewMode.TwoD ? '2D' : '3D' }}
              </button>
            }
          </div>
          @if (state.activeItem(); as item) {
            <div class="ipe:absolute ipe:top-6 ipe:left-6 ipe:flex ipe:bg-black ipe:text-white ipe:text-sm ipe:font-semibold ipe:rounded-sm ipe:overflow-hidden ipe:shadow-lg ipe:z-10">
              <button (click)="state.remove(item)"
                      class="ipe:px-4 ipe:py-2 ipe:transition-colors ipe:hover:bg-gray-500 ipe:cursor-pointer">
                {{ 'composition.remove' | translate }}
              </button>
            </div>
          }
        </section>

        <aside class="ipe:bg-white ipe:flex ipe:flex-col ipe:border-l ipe:border-[#ede8e3] ipe:min-h-0">
          <app-composer-panel-tabs class="ipe:shrink-0"/>
          <div class="ipe:flex-1 ipe:min-h-0 ipe:overflow-y-auto">
            @if (panelState.activeTab() === PanelTab.Architecture) {
              <app-element-selector></app-element-selector>
            }
            @if (panelState.activeTab() === PanelTab.Style) {
              <app-composition-customization/>
            }
            @if (panelState.activeTab() === PanelTab.Customization) {
              <app-element-customization/>
            }
            @if (panelState.activeTab() === PanelTab.Pricing && pricingFooterVisible()) {
              <app-composition-pricing/>
            }
          </div>
          @if (panelState.activeTab() !== PanelTab.Pricing && pricingFooterVisible()) {
            <app-composer-footer class="ipe:shrink-0 ipe:bg-white"/>
          }
        </aside>
      </div>
    }
  `,
  styles: [`
    @import 'tailwindcss' prefix(ipe);
    :host { display: flex; flex: 1; height: 100% }
  `],
  encapsulation: ViewEncapsulation.ShadowDom,
})
export class ComposerShellComponent implements OnDestroy {
  private readonly route = inject(ActivatedRoute);

  readonly panelState = inject(ComposerPanelStateService);
  readonly PanelTab = ComposerPanelTab;
  readonly state = inject(ComposerStateService);
  readonly vesta = inject(VestaStateService);
  readonly selection = inject(CompositionSelectionService);
  private configService = inject(ModelConfigService);
  private authService = inject(AuthService);

  readonly viewMode = signal<ComposerViewMode>(ComposerViewMode.TwoD);
  readonly ComposerViewMode = ComposerViewMode;
  readonly hasComposition = computed(() => this.state.composition().length > 0);
  readonly isModelLoading = computed(() => this.modelLoadState().loading);
  readonly pricingFooterVisible = computed(() => {
    return this.authService.currentUserPermission.canSeeConfigurationsPrice;
  })

  private readonly placementSuffixMap = computed(() => this.selection.placementSuffixMap());
  private lastVestaBaseSet: Array<{
    id: string;
    teapotId: string;
    config: string[];
  }> = [];

  readonly modelLoadState = toSignal(
    this.route.paramMap.pipe(
      map(pm => ({modelId: pm.get('modelId'), compositionId: pm.get('compositionId')})),
      filter((params): params is { modelId: string; compositionId: any | null } => !!params.modelId),
      distinctUntilChanged((a, b) => a.modelId === b.modelId && a.compositionId === b.compositionId),
      switchMap(({ modelId, compositionId }) => {
        const baseUrl = `${environment.api.get()}/modular_structure/${encodeURIComponent(modelId)}/detail`;
        const url = compositionId ? `${baseUrl}/${encodeURIComponent(compositionId)}` : baseUrl;
        return this.configService.load(url, compositionId).pipe(
          map(() => ({ loading: false, error: null })), // Replace compositionId if null on load
          startWith({ loading: true, error: null }),
          catchError(error => of({ loading: false, error }))
        );
      })
    ),
    { initialValue: { loading: true, error: null as unknown } }
  );


  constructor() {
    this.vestaUpdate();
  }

  ngOnDestroy(): void {
    this.state.reset();
    this.panelState.reset();
  }

  private vestaUpdate(): void {
    effect(() => {
      const baseSet = this.state.vestaSet();
      const suffixMap = this.placementSuffixMap();

      const baseSetSnapshot = baseSet.map(item => ({
        id: item.id,
        teapotId: item.teapotId,
        config: [...(item.config ?? [])],
      }));

      const diffFromPrevious = baseSetSnapshot.map(item => {
        const previous = this.lastVestaBaseSet.find(x => x.id === item.id);

        return {
          id: item.id,
          teapotId: item.teapotId,
          diff: IpeDebug.arrayDiff(previous?.config ?? [], item.config),
        };
      });

      this.lastVestaBaseSet = baseSetSnapshot.map(item => ({
        id: item.id,
        teapotId: item.teapotId,
        config: [...item.config],
      }));

      if (!baseSet.length) {
        IpeDebug.event('vesta.update.empty', {
          baseSet: baseSetSnapshot,
          suffixMapSize: suffixMap.size,
          suffixMap: Array.from(suffixMap.entries()),
        });

        this.vesta.applyViewUpdate({ set: [] });
        return;
      }

      const missingSuffixByItem = baseSet.map(item => ({
        id: item.id,
        teapotId: item.teapotId,
        missingSuffix: item.config.filter(plc => !suffixMap.get(plc)?.trim()),
      }));

      const set = baseSet.map(item => ({
        ...item,
        config: item.config.map(plc => {
          const suffix = suffixMap.get(plc)?.trim();
          return suffix ? `${plc}-${suffix}` : `${plc}-XXXXX-XXXXX`;
        })
      }));

      IpeDebug.event('vesta.update', {
        baseSet: baseSetSnapshot,
        diffFromPrevious,
        suffixMapSize: suffixMap.size,
        suffixMap: Array.from(suffixMap.entries()),
        missingSuffixByItem,
        finalSet: set.map(item => ({
          id: item.id,
          teapotId: item.teapotId,
          config: [...item.config],
        })),
      });

      this.vesta.applyViewUpdate({ set });
    });
  }
}
